import React, { useState, useMemo, useEffect, useCallback } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { IoIosArrowDropdown } from "react-icons/io";
import { RiResetLeftLine } from "react-icons/ri";
import { getCurrentFY, getFinancialYearMonthsPassed } from "../../Utilitie";
import Loading from "../../Extra/Loading";
const monthsRef = [
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
  "Jan",
  "Feb",
  "Mar",
];
const AFController = ({
  setFilteredAF,
  openStates,
  setOpenStates,
  setAFData,
}) => {
  // State for search inputs (one per dropdown)
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [searchValues, setSearchValues] = useState({
    Year: "",
    "Asset Group": "",
    "Main Cause": "",
    "Sub Cause": "",
    Month: "",
    "Line Up Down": "",
    Section: "",
    "COA Section": "",
    Shift: "",
    "Block Station": "",
  });
  const [selectedFY, setSelectedFY] = useState(new Set());
  const [selectedAssetGroup, setSelectedAssetGroup] = useState(new Set());
  const [selectedMainCause, setSelectedMainCause] = useState(new Set());
  const [selectedSubCause, setSelectedSubCause] = useState(new Set());
  const [selectedMonths, setSelectedMonths] = useState(new Set());
  const [selectedUpDown, setSelectedUpDown] = useState(new Set());
  const [selectedSection, setSelectedSection] = useState(new Set());
  const [selectedCoaSection, setSelectedCoaSection] = useState(new Set());
  const [selectedBlockSectionStation, setSelectedBlockSectionStation] =
    useState(new Set());
  const [selectedShift, setSelectedShift] = useState(new Set());
  // //////////////////////////////////////////////////////////////////////////////
  //                                  FETCHING DATA
  // ///////////////////////////////////////////////////////////////////////////////
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(false);
    const url = `${process.env.REACT_APP_URL_PREFIX}getassetsfailur`;
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      setData(data);
      setAFData(data);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ////////////////////////////////////////////////////////////////////////

  const toggleSelection = (setFunc, currentSet, value) => {
    const newSet = new Set(currentSet);
    newSet.has(value) ? newSet.delete(value) : newSet.add(value);
    setFunc(newSet);
  };

  const toggleDropdown = (title) => {
    setOpenStates((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  const handleSearchChange = (title, value) => {
    setSearchValues((prev) => ({
      ...prev,
      [title]: value,
    }));
  };

  const filteredData = useMemo(() => {
    if (!data?.data) return [];
    return data.data
      .filter((entry) => selectedFY.has(entry.F_YEAR))
      .map((entry) => {
        const filteredCommodities = (entry.children || [])
          .filter((group) => selectedAssetGroup.has(group.ASSET_GROUP))
          .map((group) => {
            const filteredGroupCommodities = (group.children || [])
              .filter((subGroup) => selectedMainCause.has(subGroup.MAIN_CAUSE))
              .map((subGroup) => {
                const filteredSubCommodities = (subGroup.children || [])
                  .filter((comm) => selectedSubCause.has(comm.SUB_CAUSE))
                  .map((comm) => {
                    const filteredMonths = (comm.children || [])
                      .filter((month) => selectedMonths.has(month.A_MONTH))
                      .map((month) => {
                        const filteredInnerData = (month.data || []).filter(
                          (d) =>
                            selectedSection.has(d.SECTION_NAME) &&
                            selectedCoaSection.has(d.COA_SECTION) &&
                            selectedBlockSectionStation.has(
                              d.BLOCK_SECTION_STATION,
                            ) &&
                            selectedUpDown.has(d.UP_DOWN) && // Removed size === 0 condition to require UP_DOWN selection
                            selectedShift.has(d.SHIFT),
                        );
                        return { ...month, data: filteredInnerData };
                      })
                      .filter((month) => month.data.length > 0);
                    return { ...comm, children: filteredMonths };
                  })
                  .filter((comm) => comm.children.length > 0);
                return {
                  ...subGroup,
                  children: filteredSubCommodities,
                };
              })
              .filter((subGroup) => subGroup.children.length > 0);
            return {
              ...group,
              children: filteredGroupCommodities,
            };
          })
          .filter((group) => group.children.length > 0);
        return { ...entry, children: filteredCommodities };
      })
      .filter((entry) => entry.children.length > 0);
  }, [
    data,
    selectedFY,
    selectedAssetGroup,
    selectedMainCause,
    selectedSubCause,
    selectedMonths,
    selectedUpDown,
    selectedSection,
    selectedCoaSection,

    selectedBlockSectionStation,
    selectedShift,
  ]);

  const availableFY = useMemo(() => {
    return [...new Set((data?.data || []).map((d) => d.F_YEAR))];
  }, [data]);

  const availableAssetGroup = useMemo(() => {
    return [
      ...new Set(
        (data?.data || [])
          .filter((entry) => selectedFY.has(entry.F_YEAR))
          .flatMap((entry) => entry.children || [])
          .map((group) => group.ASSET_GROUP),
      ),
    ];
  }, [data, selectedFY]);

  const availableMainCause = useMemo(() => {
    return [
      ...new Set(
        (data?.data || [])
          .filter((entry) => selectedFY.has(entry.F_YEAR))
          .flatMap((entry) => entry.children || [])
          .filter((group) => selectedAssetGroup.has(group.ASSET_GROUP))
          .flatMap((group) => group.children || [])
          .map((subGroup) => subGroup.MAIN_CAUSE),
      ),
    ];
  }, [data, selectedFY, selectedAssetGroup]);

  const availableSubCause = useMemo(() => {
    return [
      ...new Set(
        (data?.data || [])
          .filter((entry) => selectedFY.has(entry.F_YEAR))
          .flatMap((entry) => entry.children || [])
          .filter((group) => selectedAssetGroup.has(group.ASSET_GROUP))
          .flatMap((group) => group.children || [])
          .filter((subGroup) => selectedMainCause.has(subGroup.MAIN_CAUSE))
          .flatMap((subGroup) => subGroup.children || [])
          .map((comm) => comm.SUB_CAUSE),
      ),
    ];
  }, [data, selectedFY, selectedAssetGroup, selectedMainCause]);

  const availableMonths = useMemo(() => {
    return [
      ...new Set(
        (data?.data || [])
          .filter((entry) => selectedFY.has(entry.F_YEAR))
          .flatMap((entry) => entry.children || [])
          .filter((group) => selectedAssetGroup.has(group.ASSET_GROUP))
          .flatMap((group) => group.children || [])
          .filter((subGroup) => selectedMainCause.has(subGroup.MAIN_CAUSE))
          .flatMap((subGroup) => subGroup.children || [])
          .filter((comm) => selectedSubCause.has(comm.SUB_CAUSE))
          .flatMap((comm) => comm.children || [])
          .map((m) => m.A_MONTH),
      ),
    ];
  }, [
    data,
    selectedFY,
    selectedAssetGroup,
    selectedMainCause,
    selectedSubCause,
  ]);

  availableMonths.sort((a, b) => monthsRef.indexOf(a) - monthsRef.indexOf(b));

  const availableUpDown = useMemo(() => {
    return [
      ...new Set(
        (data?.data || [])
          .filter((entry) => selectedFY.has(entry.F_YEAR))
          .flatMap((entry) => entry.children || [])
          .filter((group) => selectedAssetGroup.has(group.ASSET_GROUP))
          .flatMap((group) => group.children || [])
          .filter((subGroup) => selectedMainCause.has(subGroup.MAIN_CAUSE))
          .flatMap((subGroup) => subGroup.children || [])
          .filter((comm) => selectedSubCause.has(comm.SUB_CAUSE))
          .flatMap((comm) => comm.children || [])
          .filter((month) => selectedMonths.has(month.A_MONTH))
          .flatMap((month) => month.data || [])
          .map((d) => d.UP_DOWN),
      ),
    ];
  }, [
    data,
    selectedFY,
    selectedAssetGroup,
    selectedMainCause,
    selectedSubCause,
    selectedMonths,
  ]);
  const availableSection = useMemo(() => {
    return [
      ...new Set(
        (data?.data || [])
          .filter((entry) => selectedFY.has(entry.F_YEAR))
          .flatMap((entry) => entry.children || [])
          .filter((group) => selectedAssetGroup.has(group.ASSET_GROUP))
          .flatMap((group) => group.children || [])
          .filter((subGroup) => selectedMainCause.has(subGroup.MAIN_CAUSE))
          .flatMap((subGroup) => subGroup.children || [])
          .filter((comm) => selectedSubCause.has(comm.SUB_CAUSE))
          .flatMap((comm) => comm.children || [])
          .filter((month) => selectedMonths.has(month.A_MONTH))
          .flatMap((month) =>
            (month.data || []).filter(
              (d) => selectedUpDown.has(d.UP_DOWN), // Removed size === 0 condition to require UP_DOWN selection
            ),
          )
          .map((d) => d.SECTION_NAME),
      ),
    ];
  }, [
    data,
    selectedFY,
    selectedAssetGroup,
    selectedMainCause,
    selectedSubCause,
    selectedMonths,
    selectedUpDown,
  ]);
  const availableCoaSection = useMemo(() => {
    return [
      ...new Set(
        (data?.data || [])
          .filter((entry) => selectedFY.has(entry.F_YEAR))
          .flatMap((entry) => entry.children || [])
          .filter((group) => selectedAssetGroup.has(group.ASSET_GROUP))
          .flatMap((group) => group.children || [])
          .filter((subGroup) => selectedMainCause.has(subGroup.MAIN_CAUSE))
          .flatMap((subGroup) => subGroup.children || [])
          .filter((comm) => selectedSubCause.has(comm.SUB_CAUSE))
          .flatMap((comm) => comm.children || [])
          .filter((month) => selectedMonths.has(month.A_MONTH))
          .flatMap((month) =>
            (month.data || []).filter(
              (d) =>
                selectedUpDown.has(d.UP_DOWN) &&
                selectedSection.has(d.SECTION_NAME), // Removed size === 0 condition to require UP_DOWN selection
            ),
          )
          .map((d) => d.COA_SECTION),
      ),
    ];
  }, [
    data,
    selectedFY,
    selectedAssetGroup,
    selectedMainCause,
    selectedSubCause,
    selectedMonths,
    selectedUpDown,
    selectedSection,
  ]);

  const availableShift = useMemo(() => {
    return [
      ...new Set(
        (data?.data || [])
          .filter((entry) => selectedFY.has(entry.F_YEAR))
          .flatMap((entry) => entry.children || [])
          .filter((group) => selectedAssetGroup.has(group.ASSET_GROUP))
          .flatMap((group) => group.children || [])
          .filter((subGroup) => selectedMainCause.has(subGroup.MAIN_CAUSE))
          .flatMap((subGroup) => subGroup.children || [])
          .filter((comm) => selectedSubCause.has(comm.SUB_CAUSE))
          .flatMap((comm) => comm.children || [])
          .filter((month) => selectedMonths.has(month.A_MONTH))
          .flatMap((month) =>
            (month.data || [])
              .filter(
                (d) =>
                  selectedSection.has(d.SECTION_NAME) &&
                  selectedCoaSection.has(d.COA_SECTION) &&
                  selectedUpDown.has(d.UP_DOWN), // Updated to require UP_DOWN selection
              )
              .map((d) => d.SHIFT),
          ),
      ),
    ];
  }, [
    data,
    selectedFY,
    selectedAssetGroup,
    selectedMainCause,
    selectedSubCause,
    selectedMonths,
    selectedSection,
    selectedCoaSection,
    selectedUpDown,
  ]);
  const availableBlockSectionStation = useMemo(() => {
    return [
      ...new Set(
        (data?.data || [])
          .filter((entry) => selectedFY.has(entry.F_YEAR))
          .flatMap((entry) => entry.children || [])
          .filter((group) => selectedAssetGroup.has(group.ASSET_GROUP))
          .flatMap((group) => group.children || [])
          .filter((subGroup) => selectedMainCause.has(subGroup.MAIN_CAUSE))
          .flatMap((subGroup) => subGroup.children || [])
          .filter((comm) => selectedSubCause.has(comm.SUB_CAUSE))
          .flatMap((comm) => comm.children || [])
          .filter((month) => selectedMonths.has(month.A_MONTH))
          .flatMap((month) =>
            (month.data || [])
              .filter(
                (d) =>
                  selectedSection.has(d.SECTION_NAME) &&
                  selectedCoaSection.has(d.COA_SECTION) &&
                  selectedUpDown.has(d.UP_DOWN), // Updated to require UP_DOWN selection
              )
              .map((d) => d.BLOCK_SECTION_STATION),
          ),
      ),
    ];
  }, [
    data,
    selectedFY,
    selectedAssetGroup,
    selectedMainCause,
    selectedSubCause,
    selectedMonths,
    selectedSection,
    selectedCoaSection,
    availableShift,
    selectedUpDown,
  ]);

  const defaultSetup = () => {
    if (!data?.data?.length) return;

    const allFY = new Set(data.data.map((d) => d.F_YEAR));
    const allAssetGroup = new Set(
      data.data
        .flatMap((d) => d.children || [])
        .map((group) => group.ASSET_GROUP),
    );
    const allMainCause = new Set(
      data.data
        .flatMap((d) => d.children || [])
        .flatMap((group) => group.children || [])
        .map((subGroup) => subGroup.MAIN_CAUSE),
    );
    const allSubCause = new Set(
      data.data
        .flatMap((d) => d.children || [])
        .flatMap((group) => group.children || [])
        .flatMap((subGroup) => subGroup.children || [])
        .map((comm) => comm.SUB_CAUSE),
    );
    const allMonths = new Set(
      data.data
        .flatMap((d) => d.children || [])
        .flatMap((group) => group.children || [])
        .flatMap((subGroup) => subGroup.children || [])
        .flatMap((comm) => comm.children || [])
        .map((m) => m.A_MONTH),
    );
    const allUpDown = new Set(
      data.data
        .flatMap((d) => d.children || [])
        .flatMap((group) => group.children || [])
        .flatMap((subGroup) => subGroup.children || [])
        .flatMap((comm) => comm.children || [])
        .flatMap((m) => m.data || [])
        .map((d) => d.UP_DOWN),
    );
    const allSection = new Set(
      data.data
        .flatMap((d) => d.children || [])
        .flatMap((group) => group.children || [])
        .flatMap((subGroup) => subGroup.children || [])
        .flatMap((comm) => comm.children || [])
        .flatMap((m) => m.data || [])
        .map((d) => d.SECTION_NAME),
    );
    const allCOASection = new Set(
      data.data
        .flatMap((d) => d.children || [])
        .flatMap((group) => group.children || [])
        .flatMap((subGroup) => subGroup.children || [])
        .flatMap((comm) => comm.children || [])
        .flatMap((m) => m.data || [])
        .map((d) => d.COA_SECTION),
    );
    const allBlockStation = new Set(
      data.data
        .flatMap((d) => d.children || [])
        .flatMap((group) => group.children || [])
        .flatMap((subGroup) => subGroup.children || [])
        .flatMap((comm) => comm.children || [])
        .flatMap((m) => m.data || [])
        .map((d) => d.BLOCK_SECTION_STATION),
    );
    const allShift = new Set(
      data.data
        .flatMap((d) => d.children || [])
        .flatMap((group) => group.children || [])
        .flatMap((subGroup) => subGroup.children || [])
        .flatMap((comm) => comm.children || [])
        .flatMap((m) => m.data || [])
        .map((d) => d.SHIFT),
    );
    const monthsPassed = getFinancialYearMonthsPassed(getCurrentFY(), false);
    setSelectedFY(allFY);
    setSelectedAssetGroup(allAssetGroup);
    setSelectedMainCause(allMainCause);
    setSelectedSubCause(allSubCause);
    setSelectedMonths(monthsPassed);
    setSelectedUpDown(allUpDown);
    setSelectedSection(allSection);
    setSelectedCoaSection(allCOASection);
    setSelectedBlockSectionStation(allBlockStation);
    setSelectedShift(allShift);
    setOpenStates({
      Year: false,
      "Asset Group": false,
      "Main Cause": false,
      "Sub Cause": false,
      Month: false,
      "Line Up Down": false,
      Shift: false,
      Section: false,
      "COA Section": false,
      "Block Station": false,
    });
  };

  useEffect(() => {
    defaultSetup();
  }, [data]);

  const renderCheckboxList = (title, items, selectedSet, setFunc) => {
    // Filter items based on search value
    const filteredItems = items.filter((item) =>
      item
        ?.toString()
        .toLowerCase()
        .includes(searchValues[title].toLowerCase()),
    );

    return (
      <div className={`col-md  mt-2 p-1 `} style={{}}>
        <div
          className="bg-light "
          style={{ position: "relative", fontSize: 12, borderRadius: "5px" }}
        >
          <div
            className="papaDiv "
            onClick={() => toggleDropdown(title)}
            style={{
              border: "1px solid #ccc",
              padding: "8px",
              cursor: "pointer",
              userSelect: "none",
            }}
          >
            Select {title}  
            <IoIosArrowDropdown
              style={{
                fontSize: 15,
                transition: "transform 0.3s ease",
                transform: openStates[title]
                  ? "rotate(180deg)"
                  : "rotate(0deg)",
              }}
            />
          </div>
          {openStates[title] && (
            <div
              className="papaDiv"
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                border: "1px solid #ccc",
                background: "#fff",
                zIndex: 10,
              }}
            >
              <strong>{title}</strong>
              <input
                style={{ height: "25px", width: "90%" }}
                className="form-control ms-2 mb-1 me-2"
                type="search"
                placeholder={`Search ${title}...`}
                aria-label={`Search ${title}`}
                value={searchValues[title]}
                onChange={(e) => handleSearchChange(title, e.target.value)}
              />
              <hr className="m-0  p-0" />
              <div
                className="row suggestion mt-1"
                key={filteredItems.length}
                style={{
                  maxHeight: "250px",
                  minHeight: "50px",
                  overflowY: "auto",
                }}
              >
                {filteredItems.length > 0 ? (
                  filteredItems.map((item, index) => (
                    <div
                      key={index}
                      className={`${
                        title === "Line Up Down" || title === "Month"
                          ? "col-md-6"
                          : ""
                      } fade-in`}
                      style={{ fontSize: "12px" }}
                    >
                      <label
                        className="ms-1 text-start"
                        key={item}
                        style={{ display: "block" }}
                      >
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={selectedSet.has(item)}
                          onChange={() =>
                            toggleSelection(setFunc, selectedSet, item)
                          }
                        />
                        {item}
                      </label>
                    </div>
                  ))
                ) : (
                  <div className="col-md-12 text-center">No items found</div>
                )}
              </div>
              <hr className="m-0 p-0" />
              <div className="d-flex justify-content-evenly mt-1 ">
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => setFunc(new Set(items))}
                >
                  Select All
                </button>
                <button
                  className="btn btn-sm btn-outline-success"
                  onClick={() => setFunc(new Set())}
                >
                  Clear All
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  useEffect(() => {
    setFilteredAF(filteredData);
  }, [filteredData, setFilteredAF]);
  return (
    <div className="  m-3">
      {loading && <Loading />}
      <div
        className="row"
        style={{
          border: "1px solid  #ccc ",
          background: "rgba(255, 255, 255, 0.3)", // less opaque for more blur effect
          boxShadow: "5px 5px 20px rgba(7, 7, 7, 0.3)",
          backdropFilter: "blur(15px)", // applies the blur
          WebkitBackdropFilter: "blur(10px)", // for Safari support
        }}
      >
        {renderCheckboxList("Year", availableFY, selectedFY, setSelectedFY)}
        {renderCheckboxList(
          "Asset Group",
          availableAssetGroup,
          selectedAssetGroup,
          setSelectedAssetGroup,
        )}
        {renderCheckboxList(
          "Main Cause",
          availableMainCause,
          selectedMainCause,
          setSelectedMainCause,
        )}
        {renderCheckboxList(
          "Sub Cause",
          availableSubCause,
          selectedSubCause,
          setSelectedSubCause,
        )}
        {renderCheckboxList(
          "Month",
          availableMonths,
          selectedMonths,
          setSelectedMonths,
        )}
        {renderCheckboxList(
          "Shift",
          availableShift,
          selectedShift,
          setSelectedShift,
        )}
        {renderCheckboxList(
          "Line Up Down",
          availableUpDown,
          selectedUpDown,
          setSelectedUpDown,
        )}
        {renderCheckboxList(
          "Section",
          availableSection,
          selectedSection,
          setSelectedSection,
        )}
        {renderCheckboxList(
          "COA Section",
          availableCoaSection,
          selectedCoaSection,
          setSelectedCoaSection,
        )}
        {renderCheckboxList(
          "Block Station",
          availableBlockSectionStation,
          selectedBlockSectionStation,
          setSelectedBlockSectionStation,
        )}{" "}
        <OverlayTrigger
          delay={{ show: 100, hide: 200 }}
          overlay={<Tooltip id={`tooltip-1`}>Reset Filter</Tooltip>}
        >
          <div
            className="btn btn-sm btn-danger d-flex justify-content-center align-items-center mt-1 mb-1 "
            style={{
              maxWidth: "35px",
              boxShadow: " 3px 3px 10px rgba(52, 80, 142, 0.5) ",
            }}
            onClick={() => {
              defaultSetup();
            }}
          >
            <RiResetLeftLine style={{ fontSize: "15px", fontWeight: "bold" }} />
          </div>
        </OverlayTrigger>
      </div>
    </div>
  );
};

export default AFController;
